import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FunLoader, LoadingButton, StudyLesson, StudyPlanFeedback } from '@lf/ui-primitives';
import EditLesson from './EditLesson';
import {
    streamStudyPlan,
    postStudyPlanRevisionEvent,
    reviseLessonsFromFeedback,
} from '@lf/api-client/lfApiStudyPlan';

const FEEDBACK_MAX_LEN = 400;
const MIN_NUM_LESSONS = 3;
const MAX_NUM_LESSONS = 8;

const ReviewLessons = ({
    lessons,
    originalGeneratedLessons,
    plan,
    username,
    planProp,
    setPlanProp,
    studyPlanDraftId,
    setStudyPlanDraftId,
    setLessons,
}) => {
    const [draggingDraggableId, setDraggingDraggableId] = useState(false);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);

    const [feedback, setFeedback] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);

    const emitRevisionEvent = async (eventType, beforeLessons, afterLessons, targets = []) => {
        try {
            const data = await postStudyPlanRevisionEvent({
                child_username: username,
                study_plan_draft_id: studyPlanDraftId || undefined,
                subject: plan.subject,
                goal: plan.goal,
                previous_knowledge: plan.previous_knowledge,
                needs: plan.needs,
                step: 'lessons',
                event_source: 'user',
                event_type: eventType,
                target_lesson_ids: targets,
                before_lessons: beforeLessons,
                after_lessons: afterLessons,
            });
            if (data?.study_plan_draft_id) {
                setStudyPlanDraftId(data.study_plan_draft_id);
            }
        }
        catch (error) {
            console.error('Error recording lesson revision event:', error);
        }
    };

    const handleLessonSelect = (index) => {
        if (!isReorderMode) {
            if (selectedLesson === index) {
                setSelectedLesson(null);
            } else {
                setSelectedLesson(index);
            }
        }
    };

    const handleEditLesson = (index) => {
        setEditingLesson(lessons[index]);
        setEditingIndex(index);
        setIsEditDialogOpen(true);
    };

    const handleSaveEdit = (updatedLesson) => {
        const before = [...lessons];
        const updatedLessons = [...lessons];
        updatedLessons[editingIndex] = updatedLesson;

        setLessons(updatedLessons);
        setIsEditDialogOpen(false);
        setEditingLesson(null);
        setEditingIndex(null);
        void emitRevisionEvent('manual_edit', before, updatedLessons, [updatedLesson.id]);
    };

    const handleDeleteLesson = (index) => {
        const before = [...lessons];
        const updatedLessons = [...lessons];
        const [removed] = updatedLessons.splice(index, 1);
        setLessons(updatedLessons);
        setSelectedLesson(null);
        void emitRevisionEvent('manual_delete', before, updatedLessons, removed?.id ? [removed.id] : []);
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;
        setDraggingDraggableId('');

        const before = [...lessons];
        const updatedLessons = [...lessons];

        if (!destination) {
            const [removed] = updatedLessons.splice(source.index, 1);
            setLessons(updatedLessons);
            setSelectedLesson(null);
            void emitRevisionEvent('manual_delete', before, updatedLessons, removed?.id ? [removed.id] : []);
            return;
        }

        const [removed] = updatedLessons.splice(source.index, 1);
        updatedLessons.splice(destination.index, 0, removed);
        setLessons(updatedLessons);
        void emitRevisionEvent('manual_reorder', before, updatedLessons, updatedLessons.map((lesson) => lesson.id));
    };

    const onDragUpdate = (update) => {
        setDraggingDraggableId(!update.destination ? update.draggableId : '');
    };

    const handleRegenerateFromFeedback = async () => {
        if (!feedback.trim()) return;

        setIsRegenerating(true);
        try {
            const response = await reviseLessonsFromFeedback({
                child_username: username,
                study_plan_draft_id: studyPlanDraftId || undefined,
                subject: plan.subject,
                goal: plan.goal,
                previous_knowledge: plan.previous_knowledge,
                needs: plan.needs,
                feedback: feedback.trim(),
                original_lessons: originalGeneratedLessons,
                working_lessons: lessons,
                target_lesson_ids: [],
                llm_name: 'gemini',
            });
            setLessons(response.lessons || []);
            if (response.study_plan_draft_id) {
                setStudyPlanDraftId(response.study_plan_draft_id);
            }
        }
        catch (error) {
            console.error('Error regenerating lessons from feedback:', error);
        }
        finally {
            setIsRegenerating(false);
        }
    };

    const incrementNumLessons = (incr) => {
        const currentNumLessons = parseInt(planProp.num_lessons, 10);
        const newNumLessons = currentNumLessons + incr;
        if (newNumLessons < MIN_NUM_LESSONS || newNumLessons > MAX_NUM_LESSONS) {
            return null;
        }
        const newState = { ...planProp, num_lessons: newNumLessons.toString() };
        setPlanProp(newState);
        return newState;
    };

    const handleMakeLonger = async () => {
        const updatedPlanProp = incrementNumLessons(1);
        if (!updatedPlanProp) return;
        const generatedLessons = await streamStudyPlan(plan, username, updatedPlanProp, 'gemini', setLessons);
        setLessons(generatedLessons);
    };

    const handleMakeShorter = async () => {
        const updatedPlanProp = incrementNumLessons(-1);
        if (!updatedPlanProp) return;
        const generatedLessons = await streamStudyPlan(plan, username, updatedPlanProp, 'gemini', setLessons);
        setLessons(generatedLessons);
    };

    const renderLesson = (lesson, index) => {
        const isSelected = selectedLesson === index;
        const lessonBoxStyle = isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-white';
        const draggableId = `lesson:${index}`;
        const isDragging = draggableId === draggingDraggableId;

        return (
            <div key={`lesson-container-${lesson.id || index}`} className="relative border border-transparent">
                <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={index}
                    isDragDisabled={!isReorderMode}
                >
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${isDragging ? 'opacity-50' : 'opacity-100'}`}
                        >
                            <div
                                className={!isReorderMode ? 'cursor-pointer' : ''}
                                onClick={() => handleLessonSelect(index)}>
                                <StudyLesson
                                    lesson={lesson}
                                    index={index}
                                    isLastLesson={index === lessons.length - 1}
                                    lessonBoxStyle={lessonBoxStyle}
                                    isReorderMode={isReorderMode}
                                />
                            </div>

                            {isSelected && (
                                <div className="absolute top-2 right-2 flex space-x-1">
                                    <LoadingButton
                                        imgSrc="/icon-edit.png"
                                        text="Edit"
                                        bgColor="white"
                                        onClick={() => handleEditLesson(index)}
                                    />
                                    <LoadingButton
                                        imgSrc="/icon-trash.png"
                                        text="Delete"
                                        bgColor="white"
                                        onClick={() => handleDeleteLesson(index)}
                                    />
                                </div>
                            )}

                            {isReorderMode && (
                                <div
                                    {...provided.dragHandleProps}
                                    className="absolute top-0 bottom-0 right-2 flex items-center justify-center h-full cursor-grab"
                                >
                                    <span className="text-2xl text-gray-500 px-2">≡</span>
                                </div>
                            )}
                        </div>
                    )}
                </Draggable>
            </div>
        );
    };

    return (
        <>
            <EditLesson
                lesson={editingLesson}
                isOpen={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                onSave={handleSaveEdit}
            />

            <div className="text-left mt-4 sm:flex sm:items-start sm:gap-4">
                <div className="sm:flex-1 sm:min-w-0">
                    <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
                        <div className="my-2 flex items-center">
                            <div className="font-semibold">Suggested lessons</div>
                            <div className="flex-grow" />
                            <LoadingButton
                                imgSrc="/icon-list-plus2.png"
                                text="Make longer"
                                onClick={handleMakeLonger}
                            />
                            <LoadingButton
                                imgSrc="/icon-list-minus2.png"
                                text="Make shorter"
                                onClick={handleMakeShorter}
                            />
                            <LoadingButton
                                imgSrc="/icon-list-reorder3.png"
                                text={isReorderMode ? 'Done Reordering' : 'Reorder Lessons'}
                                bgColor={isReorderMode ? 'white' : 'transparent'}
                                onClick={() => {
                                    setIsReorderMode(!isReorderMode);
                                    setSelectedLesson(null);
                                }}
                            />
                        </div>

                        <Droppable droppableId="lesson">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    {lessons.length === 0 && (
                                        <FunLoader
                                            title="Building your study plan..."
                                            subtitle="Generating lessons based on your goals"
                                            ariaLabel="Generating study plan lessons"
                                        />
                                    )}
                                    {lessons.map((lesson, index) => renderLesson(lesson, index))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>

                {lessons.length > 0 && (
                    <StudyPlanFeedback
                        contentType="lessons"
                        feedback={feedback}
                        maxLength={FEEDBACK_MAX_LEN}
                        placeholder="Make this easier to understand and add more geometry examples."
                        isRegenerating={isRegenerating}
                        onFeedbackChange={setFeedback}
                        onSubmit={handleRegenerateFromFeedback}
                        className="sm:mt-10 sm:w-64 md:w-96 sm:flex-shrink-0"
                    />
                )}
            </div>
        </>
    );
};

export default ReviewLessons;
